import { parse, set } from 'date-fns';
import { format, toZonedTime } from 'date-fns-tz';
import {
  AssessmentStageFragmentFragment,
  AssessmentStageKind,
  CognitiveMode,
  ProjectPageCompanyFragment,
  ProjectTeamEmailMode,
} from 'generated/graphql';
import { actorRoleLabels } from 'helpers/actorRoles';
import { projectTeamEmailToOptionsMap } from 'helpers/projectTeamEmailMode';
import * as yup from 'yup';
import { STAGE_NAME_MAX_LENGTH, stageKindLabel } from '../helpers';
import { IStageFormValues } from './StageForm.interfaces';

export const createValidationSchema = (projectRolesLength: number) => {
  const validationSchema = yup.object().shape({
    stageName: yup
      .string()
      .required()
      .max(
        STAGE_NAME_MAX_LENGTH,
        `Must be ${STAGE_NAME_MAX_LENGTH} characters or less`,
      ),
    stageStartTime: yup.string().required(),
    managerReportEmailLinks: yup.boolean(),
    stageEndTime: yup
      .string()
      .test(
        'stageEndTimeRequirement',
        'Stage end time is required',
        (value, { parent }) => {
          if (parent.stageIsEndTimeNull) {
            return true;
          }

          return Boolean(value);
        },
      ),

    stageProjectRoles: yup.array().when('stageMode.value', {
      is: (value: ProjectTeamEmailMode) =>
        value === ProjectTeamEmailMode.ProjectRoleBased ||
        value === ProjectTeamEmailMode.ProjectRoleAndAssigneeBased,
      then: yup
        .array()
        .of(
          yup.object().shape({
            value: yup.string().required(),
          }),
        )
        .test('is-not-empty', 'This is a required field.', (value) =>
          Boolean(value && value.length > 0),
        )
        .test(
          'is-not-all-roles',
          'You cannot select all hiring roles. Remove some hiring roles or choose another setting.',
          (value) => Boolean(value && value.length < projectRolesLength),
        ),
    }),
    managerReport: yup
      .object()
      .nullable()
      .test(
        'non-empty-report',
        { value: 'Report is required' },
        (value, { parent }) => {
          if (parent.managerReportGenerate === true && !value?.value) {
            return false;
          }

          return true;
        },
      ),
    candidateReport: yup
      .object()
      .nullable()
      .test(
        'non-empty-report',
        { value: 'Report is required' },
        (value, { parent }) => {
          if (parent.candidateReportGenerate === true && !value?.value) {
            return false;
          }

          return true;
        },
      ),
  });

  return validationSchema;
};

export const getInitialValuesForEditStage = (
  stage: AssessmentStageFragmentFragment,
  company?: ProjectPageCompanyFragment | null,
): IStageFormValues => {
  const stageMode =
    stage.emailSettings.projectTeamEmailSettings.mode ||
    ProjectTeamEmailMode.Disabled;

  const companyManagerReport = company?.managerReport?.id
    ? {
        value: company?.managerReport.id.toString(),
        label: company?.managerReport.name,
        labelDescription: 'Default',
      }
    : null;

  const companyCandidateReport = company?.candidateReport?.id
    ? {
        value: company?.candidateReport.id.toString(),
        label: company?.candidateReport.name,
        labelDescription: 'Default',
      }
    : null;

  return {
    stageName: stage?.name,
    stageStartTime: stage.startTime
      ? new Date(stage.startTime).toISOString()
      : convertToUTC(new Date().toDateString()),
    // Set stage end time to 00:00:00 instead of 23:59:59 because the datepicker defaults to the next day
    // This will not affect sending 23:59 as the correct end time in the API request
    stageEndTime: stage.endTime ? convertToUTC(stage.endTime) : '',
    stageIsEndTimeNull: !stage.endTime,
    stageEmailCandidateReport: stage.emailSettings.emailCandidateReport,

    stageMode: projectTeamEmailToOptionsMap[stageMode],
    stageProjectRoles:
      stage.emailSettings.projectTeamEmailSettings.actorRoles.map((role) => ({
        value: role,
        label: actorRoleLabels[role],
      })),
    stageAssessmentExpiry: stage.assessmentExpiryOption
      ? {
          value: stage.assessmentExpiryOption?.duration?.toString(),
          label: `${stage.assessmentExpiryOption?.duration} days`,
        }
      : {
          value: 'none',
          label: 'Never expires',
        },
    cognitiveMode: stage.cognitiveMode || CognitiveMode.InDepth,
    managerReportGenerate: Boolean(stage.emailSettings.managerReport?.id),
    candidateReportGenerate: Boolean(stage.emailSettings.candidateReport?.id),
    managerReport: stage.emailSettings.managerReport?.id
      ? {
          value: stage.emailSettings.managerReport.id.toString(),
          label: stage.emailSettings.managerReport.name,
          labelDescription:
            stage.emailSettings.managerReport.id === company?.managerReport?.id
              ? 'Default'
              : undefined,
        }
      : null,
    candidateReport: stage.emailSettings.candidateReport?.id
      ? {
          value: stage.emailSettings.candidateReport.id.toString(),
          label: stage.emailSettings.candidateReport.name,
          labelDescription:
            stage.emailSettings.candidateReport.id ===
            company?.candidateReport?.id
              ? 'Default'
              : undefined,
        }
      : null,
    companyDefaultManagerReport: companyManagerReport,
    companyDefaultCandidateReport: companyCandidateReport,
    stageKind: {
      value: stage.kind,
      label: stageKindLabel(stage.kind),
    },
  };
};

export const getInitialValuesForCreateStage = (
  company?: ProjectPageCompanyFragment | null,
  cognitiveMode?: CognitiveMode,
  // A stage is created as a plain assessment unless the AI interview entry in
  // the Insert stage menu was used; the form has no kind field of its own.
  kind: AssessmentStageKind = AssessmentStageKind.Assessment,
): IStageFormValues => {
  const companyManagerReport = company?.managerReport?.id
    ? {
        value: company?.managerReport.id.toString(),
        label: company?.managerReport.name,
        labelDescription: 'Default',
      }
    : null;

  const companyCandidateReport = company?.candidateReport?.id
    ? {
        value: company?.candidateReport.id.toString(),
        label: company?.candidateReport.name,
        labelDescription: 'Default',
      }
    : null;

  return {
    stageName: stageKindLabel(kind),
    stageEmailCandidateReport: Boolean(company?.emailCandidateReport),
    stageStartTime: new Date().toISOString(),
    stageEndTime: '',
    stageIsEndTimeNull: true,
    stageMode:
      projectTeamEmailToOptionsMap[
        company?.projectTeamEmailSettings.mode || ProjectTeamEmailMode.Disabled
      ],
    stageProjectRoles:
      company?.projectTeamEmailSettings.actorRoles.map((role) => ({
        value: role,
        label: actorRoleLabels[role],
      })) || [],
    stageAssessmentExpiry: {
      value: 'none',
      label: 'Never expires',
    },
    managerReportGenerate: Boolean(company?.generateManagerReport),
    candidateReportGenerate: Boolean(company?.generateCandidateReport),
    managerReport: companyManagerReport,
    candidateReport: companyCandidateReport,
    cognitiveMode: cognitiveMode || CognitiveMode.InDepth,
    companyDefaultManagerReport: companyManagerReport,
    companyDefaultCandidateReport: companyCandidateReport,
    stageKind: {
      value: kind,
      label: stageKindLabel(kind),
    },
  };
};

export const convertToUTC = (
  dateInput: string,
  isEndOfDay: boolean = false,
) => {
  let parsedDate;
  // Check if the input is an ISO string by looking for the 'T' and 'Z'
  if (dateInput.includes('T') && dateInput.includes('Z')) {
    parsedDate = toZonedTime(dateInput, 'UTC');
  } else {
    // Assume it is in the format 'EEE MMM dd yyyy' (e.g., 'Thu Sep 26 2024')
    parsedDate = parse(dateInput, 'EEE MMM dd yyyy', new Date());
  }

  let utcDate;
  // Set desired time
  if (isEndOfDay) {
    utcDate = set(parsedDate, { hours: 23, minutes: 59, seconds: 59 });
  } else {
    utcDate = set(parsedDate, { hours: 0, minutes: 0, seconds: 0 });
  }

  return format(utcDate, "yyyy-MM-dd'T'HH:mm:ss'Z'");
};

export const ASSESSMENT_EXPIRY_TOOLTIP_CONTENT =
  'Defines whether assessment results expire after a set validity period. Any changes to this setting will only affect candidates who complete their assessment from this point onwards.';
export const assessmentExpiryDurationOptions = [
  'none',
  '30',
  '60',
  '90',
  '120',
  '180',
  '365',
];
